// The player as Pixie's host (types/pixieHost.ts): the experience store is her
// page, and what she does goes out as the player's application events. Every
// field reads the store when Pixie reads it, so one host serves every render.
import { useMemo } from "react";
import * as analytics from "@/modules/analytics";
import { isVoiceEnabled } from "@/modules/askEnabled";
import { isVoiceAvailable } from "@/modules/assistantAvailability";
import * as chainSession from "@/modules/ChainSession";
import { eventNames, valueNames } from "@/modules/Constants";
import { readMcpScope } from "@/modules/mcpScope";
import { isMcpAppSurface } from "@/modules/mcpSurface";
import { showPixieToast } from "@/modules/pixieWorkspace";
import { resolveViewerSurface } from "@/modules/viewerSurface";
import { shopWindowNames } from "@/plugin/recipeShopWindows";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type ShopWindowConfigList from "@/plugin/ShopWindowConfigList";
import type { PixieHost, PixieLayout, PixieVideo } from "@/types/pixieHost";
import type { ExperienceState, PluginEvent } from "@/types/state";

const REGION = "Ask Shop Window";

type OnApplicationEvent = (event: PluginEvent) => void;

export type PlayerPixieHostOptions = {
  experienceState: ExperienceState;
  onApplicationEvent: OnApplicationEvent;
  /** The Ask config, which the close sends back. */
  config?: ShopWindowConfig;
  layout?: PixieLayout;
};

function methodConfig(state: ExperienceState): ShopWindowConfig | undefined {
  const configs = state.applicationData.getValue(
    valueNames.SHOP_WINDOW_CONFIG_LIST,
  ) as ShopWindowConfigList | undefined;
  return configs ? configs.atName(shopWindowNames.METHOD) : undefined;
}

function methodStep(state: ExperienceState): number | null {
  const config = methodConfig(state);
  if (!config || typeof config.index !== "number") {
    return null;
  }
  const offset = config.hasOpeningSlide(state.experienceData) ? 1 : 0;
  const step = config.index - offset + 1;
  return step >= 1 ? step : null;
}

// SHOP_WINDOW_INDEX_CHANGED (not config.index) is what makes the store seek;
// Pixie's steps count from 1, slide indexes from 0 off mainIndexStart.
function seekStep(
  state: ExperienceState,
  onApplicationEvent: OnApplicationEvent,
  step: number,
): boolean {
  const config = methodConfig(state);
  if (!config) {
    return false;
  }
  onApplicationEvent({
    name: eventNames.SHOP_WINDOW_INDEX_CHANGED,
    region: REGION,
    configName: config.name,
    index: config.mainIndexStart(state.experienceData) + (step - 1),
  });
  return true;
}

function playerVideo(
  state: ExperienceState,
  onApplicationEvent: OnApplicationEvent,
): PixieVideo {
  return {
    get title() {
      return state.experienceData.title || state.experienceData.name;
    },
    get poster() {
      return state.experienceData.getPosterImageUrl();
    },
    get currentTime() {
      return state.experienceData.nowTime;
    },
    get ended() {
      return !!state.applicationData.getValue(valueNames.VIDEO_ENDED, false);
    },
    get methodStep() {
      return methodStep(state);
    },
    openStep(step) {
      if (!seekStep(state, onApplicationEvent, step)) {
        return;
      }
      // The viewer asked to see the step, and the Workspace covers the video.
      // Seek first so the resume that closing Pixie triggers lands on the step.
      onApplicationEvent({
        name: eventNames.SHOP_WINDOW_EXIT,
        region: REGION,
        shopWindowConfig: state.applicationData.getValue(
          valueNames.SHOP_WINDOW_CONFIG,
        ),
      });
    },
    seekStep(step) {
      seekStep(state, onApplicationEvent, step);
    },
    resume() {
      onApplicationEvent({ name: eventNames.PIXIE_RESUME, region: REGION });
    },
  };
}

function createPlayerPixieHost(options: PlayerPixieHostOptions): PixieHost {
  const { experienceState: state, onApplicationEvent } = options;
  return {
    surface: resolveViewerSurface(),
    layout: options.layout,
    get voice() {
      return (
        isVoiceEnabled(state.experienceData) &&
        isVoiceAvailable() &&
        !isMcpAppSurface()
      );
    },
    get hints() {
      return state.experienceData.styleFlags.showPanelHintsView();
    },
    get logoUrl() {
      return state.experienceData.getLogoImageUrl();
    },
    get experienceId() {
      return state.experienceData.idexperience;
    },
    get features() {
      return state.experienceData.features;
    },
    video: playerVideo(state, onApplicationEvent),
    getContext() {
      return {
        // recentWatched excludes the current experience: ChainSession records
        // the one being left. Each pair carries the id so the assistant can
        // fetch that recipe's detail on demand.
        watchedRecipes: chainSession.recentWatched(12),
        publicationScope: readMcpScope(),
      };
    },
    // One conversation per load of the player: the chain session's id.
    conversationKey() {
      return chainSession.getSessionId();
    },
    playRecipe(recipe) {
      // The phone sheet and the Workspace stand in for the video, so a recipe
      // played from them says the conversation stays.
      if (options.layout) {
        showPixieToast("Your conversation stays in Pixie");
      }
      onApplicationEvent({
        name: eventNames.CHAIN_ADVANCE,
        region: REGION,
        idexperience: recipe.id,
      });
    },
    track(name, props) {
      analytics.track(name, props);
    },
    close() {
      onApplicationEvent({
        name: eventNames.SHOP_WINDOW_EXIT,
        region: REGION,
        shopWindowConfig: options.config,
      });
    },
  };
}

/**
 * The host for one mount point, rebuilt only when its options change. The
 * store passes the same live model on each change, so the host keeps its
 * identity across renders.
 */
export function usePlayerPixieHost(options: PlayerPixieHostOptions): PixieHost {
  const { experienceState, onApplicationEvent, config, layout } = options;
  return useMemo(
    () =>
      createPlayerPixieHost({
        experienceState,
        onApplicationEvent,
        config,
        layout,
      }),
    [experienceState, onApplicationEvent, config, layout],
  );
}
